import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { CheckBox } from "components";
import { UserCircleIcon } from "components/icons";
import { ToggleButton } from "components/toggle";
import {
  useCustomerUserSearch,
  useGetAllCustomers,
  usePermissionStore,
} from "controller";
import { CustomerOnBoardingDrawer } from "pages/modules/customer/customers/components/on-boarding-customer/CustomerOnBoardingDrawer";
import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { PERMISSIONS } from "shared/data";
import { TicketCreationType } from "../CreateTicketDrawer";
import { AddParticipantComponent } from "./AddParticipantComponent";
import { CreateTicketAutocomplete } from "./TicketAutocomplete";

export const CustomerUserSearch = ({ type }: { type: TicketCreationType }) => {
  const theme = useTheme();
  const { control } = useFormContext();

  return (
    <Box
      sx={{
        p: "12px 16px",
        width: "100%",
        borderRadius: "8px",
        backgroundColor: "#FFF",
      }}
    >
      <Stack
        direction={"row"}
        sx={{
          gap: "24px",
          backgroundColor: theme.palette.neutral["005"],
          p: "12px 0px",
          borderRadius: "4px",
        }}
        divider={<Divider orientation="vertical" flexItem />}
      >
        <CustomerSearch />
        <UserSearch type={type} />
      </Stack>
      <Stack
        direction={"row"}
        sx={{
          mt: "8px",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction={"row"} alignItems={"center"} gap="8px">
          <Controller
            name="showParticipants"
            control={control}
            render={({ field }) => (
              <CheckBox
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
          <Typography variant="body-medium">Add more participants</Typography>
        </Stack>
        <BillableToggleComponent />
      </Stack>
      <AddParticipantComponent />
    </Box>
  );
};

export const BillableToggleComponent = () => {
  const theme = useTheme();
  const { control } = useFormContext();
  // const watchBillable = watch("isBillable");
  return (
    <Controller
      control={control}
      name={"isBillable"}
      defaultValue={[]}
      render={({ field }) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          gap={"8px"}
        >
          <ToggleButton
            onChange={(value) => {
              // setValue("isBillable", value);
              field.onChange(value);
            }}
            isChecked={field.value}
          />
          <Typography
            variant="body-large"
            noWrap
            color={theme.palette.lightBg.low}
          >
            {field.value ? "Billable" : "Non Billable"}
          </Typography>
        </Stack>
      )}
    />
  );
};
const CustomerSearch = () => {
  const theme = useTheme();
  const { control, setValue: setFormValue } = useFormContext();
  const [customerQuery, setCustomerQuery] = React.useState("");
  // const { data: customers } = useCustomerSearch(customerQuery);
  const { data: customers } = useGetAllCustomers({
    sortColumn: "customer_name",
    sortDirection: "asc",
    filter: [{ column: "is_active", operator: "=", value: "true" }],
    searchQuery: customerQuery,
  });
  const haveFullAccessToCustomer = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.CUSTOMER.CUSTOMER_ALL_RW]),
  );
  const [value, setValue] = React.useState(null);
  const [isCreateCustomerDrawerOpen, setIsCreateCustomerDrawerOpen] =
    useState(false);
  return (
    <>
      {isCreateCustomerDrawerOpen && (
        // <CreateCustomerDrawer
        //   open={isCreateCustomerDrawerOpen}
        //   onClose={() => setIsCreateCustomerDrawerOpen(false)}
        // />
        <CustomerOnBoardingDrawer
          open={isCreateCustomerDrawerOpen}
          // onClose={() => setIsOnboardingDrawerOpen(false)}
          onClose={() => setIsCreateCustomerDrawerOpen(false)}
          onBoarding="customer"
        />
      )}
      <CreateTicketAutocomplete
        name="customer"
        control={control}
        fullWidth
        options={customers?.result || []}
        label="Customer"
        nodataText="No customer found"
        placeholder="Search customer"
        required
        value={value}
        labelMappingKey="customer_name"
        isOptionEqualToValue={(
          option: Record<string, unknown>,
          val: Record<string, unknown>,
        ) => option?.mapping_id === val?.mapping_id}
        onChange={(event: any, newValue: any) => {
          setValue(newValue);

          setFormValue("user", null);
          // setFormValue("ticket_site_id", null);
        }}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : `${option.customer_name}`
        }
        inputStyle={{
          color: theme.palette.lightBg.low,
        }}
        onInputChange={(event: any, newInputValue: any) => {
          setCustomerQuery(newInputValue);
        }}
        renderOptions={(props, option) => {
          return (
            <li
              {...props}
              key={option.site_id}
              style={{
                ...theme.typography["body-small"],
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <UserCircleIcon fontSize="small" />
              {option.customer_name}
            </li>
          );
        }}
        isCreatable={haveFullAccessToCustomer}
        onCreateClick={() => {
          setIsCreateCustomerDrawerOpen(true);
        }}
        createButtonText="Add New Customer"
      />
    </>
  );
};

export const UserSearch = ({ type }: { type?: TicketCreationType }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { control, watch, setValue: setFormValue } = useFormContext();
  const watchCustomer = watch("customer");
  const haveAccessToCustomer = usePermissionStore((state) =>
    state.hasPermissions(
      [
        PERMISSIONS.CUSTOMER.CUSTOMER_ALL_RW,
        PERMISSIONS.CUSTOMER.CUSTOMER_CUSTOM_RW,
      ],
      "OR",
    ),
  );
  const [customerQuery, setCustomerQuery] = React.useState("");
  const [value, setValue] = React.useState(null);
  const [isCreateUserDrawerOpen, setIsCreateUserDrawerOpen] = useState(false);
  const { data: customerUsers } = useCustomerUserSearch({
    searchQuery: customerQuery,
    ...(watchCustomer ? { customerId: watchCustomer.mapping_id } : {}),
  });

  return (
    <>
      {isCreateUserDrawerOpen && (
        // <CreateUserDrawer
        //   open={isCreateUserDrawerOpen}
        //   onClose={() => setIsCreateUserDrawerOpen(false)}
        // />
        <CustomerOnBoardingDrawer
          open={isCreateUserDrawerOpen}
          // onClose={() => setIsOnboardingDrawerOpen(false)}
          onClose={() => setIsCreateUserDrawerOpen(false)}
          onBoarding="user"
        />
      )}
      <CreateTicketAutocomplete
        name={"user"}
        control={control}
        fullWidth
        nodataText="No user found"
        options={customerUsers || []}
        label={type === "ticket" ? "User" : ""}
        placeholder={type !== "ticket" ? "Search by user" : "Search user"}
        required
        value={value}
        isOptionEqualToValue={(
          option: Record<string, unknown>,
          val: Record<string, unknown>,
        ) => option?.mapping_id === val?.mapping_id}
        onChange={(event: any, newValue: any) => {
          setValue(newValue);
          if (newValue) {
            setFormValue("customer", {
              mapping_id: newValue?.customer_id,
              customer_name: newValue?.customer_name,
              site_id: newValue?.site_id,
              site_name: newValue?.site_name,
            });
          }
        }}
        getOptionLabel={(option) =>
          typeof option === "string"
            ? option
            : `${option.first_name}${
                option.last_name ? ` ${option.last_name}` : ""
              }`
        }
        inputStyle={{
          color: theme.palette.lightBg.low,
          ...(type !== "ticket"
            ? {
                backgroundColor: theme.palette.neutral["005"],
                borderRadius: "4px",
              }
            : {}),
        }}
        onInputChange={(event: any, newInputValue: any) => {
          setCustomerQuery(newInputValue);
        }}
        renderOptions={(props, option) => {
          return (
            <li
              {...props}
              key={option.site_id}
              style={{
                ...theme.typography["body-small"],
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <UserCircleIcon fontSize="small" />
              {watchCustomer?.mapping_id
                ? `${option.first_name} ${option.last_name}`
                : `${option.customer_name}, ${option.site_name}, ${option.first_name}
              ${option.last_name}`}
            </li>
          );
        }}
        isCreatable={haveAccessToCustomer}
        createButtonText="Add New User"
        onCreateClick={() => {
          setIsCreateUserDrawerOpen(true);
        }}
        isRefreshable
        onRefreshClick={() => {
          queryClient.invalidateQueries(["customerUserDropdown"]);
        }}
      />
    </>
  );
};
